import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Layers, Map, TrendingUp, Package, BarChart3, Network } from "lucide-react";

export default function DistributorsSolution() {
  useEffect(() => {
    document.title = "For Distributors — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "Multi-brand demand intelligence for building materials distributors. See where every brand you carry is being asked about, in every territory you cover.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="distributors-solution-page">
      <section className="relative bg-[#0F2540] text-white border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-15" aria-hidden="true"/>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag text-slate-300">For Distributors</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-3 leading-[0.95]">
            Every brand. Every territory. <span className="text-[#C62828]">One workspace.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-3xl">
            LumberLoop USA gives distributors a unified view of contractor demand across every manufacturer partner and every dealer in their region — so you can direct inventory, resources, and reps to exactly where the market is pulling.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2">Talk to Sales <ArrowRight size={16}/></Link>
            <a href="#value" className="btn-ghost-light inline-flex items-center gap-2">What You Get</a>
          </div>
        </div>
        <div className="h-2 stripes-flag" aria-hidden="true"/>
      </section>

      <section id="value" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-3xl">
          <div className="label-tag">Built for Multi-Brand Complexity</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 text-[#1A365D]">
            Stop guessing where <span className="text-[#C62828]">demand is heating up.</span>
          </h2>
          <p className="mt-4 text-slate-700 max-w-2xl">
            Distributors sit at the center of the building materials channel — but usually get the least visibility. LumberLoop USA changes that.
          </p>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Layers, title: "Cross-Brand Dashboard", body: "See every manufacturer partner in one view. Compare demand, response, and conversion side-by-side." },
            { icon: Map, title: "Regional Heat Maps", body: "Where's demand growing? Where's it stalling? Get the geographic view your sales team needs." },
            { icon: TrendingUp, title: "Category Trends", body: "Which product categories are surging with contractors? Direct inventory and marketing accordingly." },
            { icon: Package, title: "Dealer Performance", body: "Which of your downstream dealers respond fastest, quote most, and win the most? Data-driven partner reviews." },
            { icon: BarChart3, title: "Sell-Through Signals", body: "Contractor demand is a leading indicator of dealer reorders. See it 30–60 days before the PO hits." },
            { icon: Network, title: "White-Label Support", body: "Enterprise plans include distributor-branded portals and reporting for your rep force and channel partners." },
          ].map(f => (
            <div key={f.title} className="brutal-card-flat bg-white p-5">
              <div className="w-11 h-11 bg-[#0F2540] text-white flex items-center justify-center"><f.icon size={20}/></div>
              <div className="font-heading font-bold uppercase mt-3 text-[#0F2540]">{f.title}</div>
              <p className="mt-2 text-sm text-slate-700">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FB] border-y border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="label-tag">The Distributor Advantage</div>
          <h2 className="font-heading text-3xl md:text-4xl font-black uppercase mt-2 text-[#1A365D]">Turn demand data into <span className="text-[#C62828]">purchasing power.</span></h2>
          <ul className="mt-6 space-y-3">
            {[
              "Justify inventory positions with real, forward-looking demand data",
              "Renegotiate manufacturer terms with actual channel performance metrics",
              "Deploy field reps to the highest-opportunity dealers",
              "Onboard new manufacturer partners with proof of contractor pull",
              "Report to leadership with numbers, not anecdotes",
            ].map(it => (
              <li key={it} className="flex items-start gap-3 text-slate-700"><CheckCircle2 size={20} className="text-[#C62828] mt-0.5 flex-shrink-0"/><span>{it}</span></li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-[#0F2540] text-white py-20 border-t-2 border-[#1A365D] text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-5xl font-black uppercase">See a distributor demo with <span className="text-[#C62828]">your brands mapped in.</span></h2>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2 mt-8">Request a Demo <ArrowRight size={16}/></Link>
        </div>
      </section>
    </div>
  );
}
